/**
 * i18n Configuration
 * Defines supported locales and default locale for the application
 */

export const locales = ['pt-BR', 'en-US'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'pt-BR';

/** localStorage key for landing / non-[locale] pages */
export const LANGUAGE_STORAGE_KEY = 'preferred-language';

/**
 * Locale display names for the language switcher
 */
export const localeNames: Record<Locale, string> = {
  'pt-BR': 'Português',
  'en-US': 'English',
};

/**
 * Currency configuration for each locale
 * Following ISO 4217 currency codes and Intl.NumberFormat standards
 */
export const localeCurrency: Record<Locale, { code: string; symbol: string }> = {
  'pt-BR': { code: 'BRL', symbol: 'R$' },
  'en-US': { code: 'USD', symbol: '$' },
};

/**
 * Default IANA timezone for each locale (used when client timezone is not provided)
 * Used for date bucketing in stats/dashboard to show sales in user's local dates
 */
export const localeTimezone: Record<Locale, string> = {
  'pt-BR': 'America/Sao_Paulo',
  'en-US': 'America/New_York',
};

/**
 * Normalize locale for URL path (e.g., 'pt-BR' -> 'pt-br')
 */
export function normalizeLocale(locale: string): string {
  return locale.toLowerCase();
}

/**
 * Denormalize locale from URL path (e.g., 'pt-br' -> 'pt-BR')
 */
export function denormalizeLocale(locale: string): Locale {
  const normalized = locale.toLowerCase();
  
  // Map normalized locale back to proper format
  if (normalized === 'pt-br') return 'pt-BR';
  if (normalized === 'en-us' || normalized === 'en') return 'en-US';
  
  // Default fallback
  return defaultLocale;
}

/**
 * Check if a locale is valid
 */
export function isValidLocale(locale: string): boolean {
  const denormalized = denormalizeLocale(locale);
  return locales.includes(denormalized);
}

/**
 * Resolve locale from URL (first segment: `pt-br`, `en-us`) or localStorage; else default.
 * Safe for SSR: no `window` → `defaultLocale` when path has no locale segment.
 */
export function resolveLandingLocale(pathname: string | null | undefined): Locale {
  if (pathname) {
    const first = pathname.split('/').filter(Boolean)[0];
    if (first && isValidLocale(first)) {
      return denormalizeLocale(first);
    }
  }
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY) as Locale | null;
    if (stored && locales.includes(stored as Locale)) {
      return stored as Locale;
    }
  }
  return defaultLocale;
}

/**
 * Replace the locale prefix in a path, or prepend locale (e.g. `/` → `/pt-br`).
 */
export function replaceLocalePrefixInPathname(pathname: string, locale: Locale): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] && isValidLocale(segments[0])) {
    segments[0] = normalizeLocale(locale);
    return '/' + segments.join('/');
  }
  return `/${normalizeLocale(locale)}`;
}

/**
 * Load translation messages for a given locale
 * This is a server-side utility function for loading messages explicitly
 */
export async function loadMessages(locale: Locale): Promise<Record<string, any>> {
  return (await import(`../../messages/${locale}.json`)).default;
}
